import { useEffect, useState } from "react";
import { api, ApiError, type AgentMe } from "./api.ts";
import { Login } from "./Login.tsx";
import { AdminDashboard } from "./AdminDashboard.tsx";

const TOKEN_KEY = "pickit-voip-admin:token";

/** When embedded inside the chatapp (`/admin/voip` tab via iframe),
 *  the parent passes its own bridged JWT as a `?token=` query param.
 *  The control-plane's auth bridge accepts these (MYEXPERT_JWT_SECRET)
 *  so we can skip the login screen entirely. */
function readEmbeddedToken(): string | null {
  try {
    const url = new URL(window.location.href);
    const t = url.searchParams.get("token");
    if (t && t.length > 20) {
      // Persist for the lifetime of the tab so subsequent navigations
      // inside the iframe don't lose auth.
      sessionStorage.setItem(TOKEN_KEY, t);
      return t;
    }
    const stored = sessionStorage.getItem(TOKEN_KEY);
    if (stored && stored.length > 20) return stored;
  } catch {
    /* ignore */
  }
  return null;
}

export function App() {
  const [token, setToken] = useState<string | null>(
    () => readEmbeddedToken() ?? localStorage.getItem(TOKEN_KEY),
  );
  const [me, setMe] = useState<AgentMe | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setMe(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.me(token);
        if (!cancelled) setMe(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setMe(null);
        } else {
          setBootError((err as Error).message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  function handleLogin(newToken: string): void {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setBootError(null);
  }
  function handleLogout(): void {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setMe(null);
  }

  if (!token) {
    return (
      <div className="app-shell--full">
        <Login onLogin={handleLogin} />
      </div>
    );
  }
  if (bootError) {
    return (
      <div className="app-shell--full">
        <div className="login-card">
          <h1 className="brand">Pickit<span className="accent">VoIP Admin</span></h1>
          <div className="toast error">{bootError}</div>
          <button className="primary" onClick={handleLogout}>Se déconnecter</button>
        </div>
      </div>
    );
  }
  if (!me) {
    return (
      <div className="app-shell--full">
        <p className="muted">Chargement…</p>
      </div>
    );
  }
  // Admin portal is admin-only — agents get the widget instead.
  if (me.agent.role !== "admin") {
    return (
      <div className="app-shell--full">
        <div className="login-card">
          <h1 className="brand">Pickit<span className="accent">VoIP Admin</span></h1>
          <div className="toast error">Accès administrateur requis.</div>
          <button className="primary" onClick={handleLogout}>Se déconnecter</button>
        </div>
      </div>
    );
  }
  return <AdminDashboard me={me} token={token} onLogout={handleLogout} />;
}
